/**
 * CopilotTurnView — one Merlin reply, rendered the same way on every Quality
 * surface (the Ask tab and the workspace conversation pane).
 *
 * The reply LEADS with the spoken rundown (it plays as it streams in), then
 * the agent's written answer — which itself leads with an "At a glance"
 * section and is followed by the detail, bundled in one message. The answer
 * reuses the Agent Console's renderer (AssistantTurn), which shows the final
 * answer prominently and keeps the thinking/tool trace behind its own "View
 * thinking trace" toggle. `speaking` is true while THIS turn's rundown plays.
 */
import { AssistantTurn, UsageMeta } from '../agentConsole/AssistantTurnView';
import { AgentFileCard } from '../agentConsole/AgentFileCard';
import { MessageTimestamp } from '../agentConsole/MessageTimestamp';
import type { FeedbackContext } from '../../services/agentConsole';
import type { CopilotTurn } from '../../pages/qualityCopilotTurns';
import { DraftCard, ReadbackCard } from '../../pages/QcpOrderCards';
import styles from './CopilotTurns.module.css';

export interface CopilotTurnViewProps {
  turn: CopilotTurn;
  onReplay: (turnId: string, spoken: string) => void;
  onStop: () => void;
  /** Opens the shared order modal on a draft card's Review (Merlin door only). */
  onReview?: (orderId: string) => void;
  speaking: boolean;
  synthesizing: boolean;
  showUsage: boolean;
  conversationId: string | null;
  strongestModelId: string | null;
  strongestModelLabel: string | null;
  /** A small "Listen" button instead of the rundown block: on the Quality
   *  portal the block repeated the written answer word for word. */
  compactVoice?: boolean;
}

export function CopilotTurnView({
  turn,
  onReplay,
  onStop,
  onReview,
  speaking,
  synthesizing,
  showUsage,
  conversationId,
  strongestModelId,
  strongestModelLabel,
  compactVoice = false,
}: CopilotTurnViewProps) {
  const {
    id,
    spoken,
    blocks,
    usage,
    streaming,
    error,
    cancelled,
    notice,
    messageId,
    model,
    readback,
    draftCard,
    agentFiles,
  } = turn;
  const hasBlocks = !!blocks && blocks.length > 0;
  // Feedback attaches to the real audit row, so only once the turn has settled
  // and we have a persisted message id + the conversation it belongs to.
  const feedback: FeedbackContext | null =
    !streaming && !error && messageId && conversationId
      ? {
          basePath: '/quality-copilot',
          conversationId,
          messageId,
          modelUsed: model ?? null,
          strongestModelId,
          strongestModelLabel,
        }
      : null;
  return (
    <div className={styles.turn}>
      {/* 0) Recovery note — the thread's history no longer fit the model, so
          this reply started from a fresh context. Calm, not an error. */}
      {notice && (
        <div className={styles.contextNote} role="status">
          <span aria-hidden="true">↻</span> {notice}
        </div>
      )}

      {/* 1) Voice rundown — leads the reply; plays the moment it streams in.
          Three states: PREPARING (TTS round-trip in flight) → spinner +
          "Preparing voice…"; PLAYING → equalizer; IDLE → "tap to play". */}
      {spoken && compactVoice && (
        <button
          type="button"
          className={styles.voiceCompact}
          onClick={() => (speaking ? onStop() : onReplay(id, spoken))}
          aria-label={speaking ? 'Stop spoken rundown' : 'Listen to a spoken rundown'}
        >
          {synthesizing ? 'Preparing…' : speaking ? '❚❚ Stop' : '▶ Listen'}
        </button>
      )}
      {spoken && !compactVoice && (
        <div
          className={`${styles.voice} ${speaking ? styles.voiceSpeaking : ''} ${
            synthesizing ? styles.voiceSynth : ''
          }`}
        >
          <button
            type="button"
            className={styles.voicePlay}
            onClick={() => (speaking ? onStop() : onReplay(id, spoken))}
            aria-label={
              synthesizing
                ? 'Preparing spoken rundown'
                : speaking
                  ? 'Stop spoken rundown'
                  : 'Play spoken rundown'
            }
          >
            {synthesizing ? (
              <span className={styles.voiceSpinner} aria-hidden="true" />
            ) : speaking ? (
              '❚❚'
            ) : (
              '▶'
            )}
          </button>
          <div className={styles.voiceMid}>
            <div className={styles.voiceTop}>
              <span className={styles.voiceLabel}>Voice rundown</span>
              {synthesizing ? (
                <span className={styles.voiceState}>Preparing voice…</span>
              ) : speaking ? (
                <span className={styles.eq} aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                </span>
              ) : (
                <span className={styles.voiceState}>tap to play</span>
              )}
            </div>
            <p className={styles.voiceCap}>{spoken}</p>
          </div>
        </div>
      )}

      {/* 1b) Read-back card — the order at a glance with the open questions.
          The speak button reuses this turn's rundown player. */}
      {readback && (
        <ReadbackCard
          data={readback}
          speaking={speaking}
          onSpeak={() =>
            speaking ? onStop() : onReplay(id, readback.spokenSummary)
          }
        />
      )}

      {/* 2) The written answer — leads with an "At a glance" section, then
          the detail. AssistantTurn renders the answer prominently and tucks
          the thinking/tool trace behind its own toggle. */}
      {(hasBlocks || streaming) && (
        <div className={styles.report}>
          <div className={styles.reportBody}>
            {hasBlocks ? (
              <>
                <AssistantTurn
                  blocks={blocks ?? []}
                  isStreaming={!!streaming}
                  feedback={feedback}
                />
                {usage && showUsage ? <UsageMeta usage={usage} /> : null}
              </>
            ) : (
              <div className={styles.status}>
                <span className={styles.spinner} />
                <span>Thinking…</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 3) Draft card — the outcome: created, NOT submitted, tap to review. */}
      {draftCard && onReview && <DraftCard data={draftCard} onReview={onReview} />}

      {/* 2b) Agent files — charts / data extracts Merlin generated and
          attached, one downloadable card each (images preview inline). */}
      {agentFiles?.map((file) => (
        <AgentFileCard
          key={file.attachmentId}
          base="/quality-copilot"
          file={file}
          conversationId={conversationId}
        />
      ))}

      {/* A user Stop is a calm terminal state, never an error: the partial
          reply above stays, and the composer is ready for the next message. */}
      {cancelled && !streaming && (
        <div className={styles.stoppedNote}>
          <span aria-hidden="true">⏹</span> Stopped — reply interrupted. You can
          keep chatting in this thread.
        </div>
      )}

      {error && <div className={styles.turnError}>{error}</div>}

      {/* When Merlin answered — persisted created_at on resume, send time live. */}
      {!streaming && turn.createdAt ? (
        <div className={styles.turnStampRow}>
          <MessageTimestamp at={turn.createdAt} />
        </div>
      ) : null}
    </div>
  );
}

export default CopilotTurnView;
